import React from 'react';
import { Stack, Typography, Button, List, ListItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks/reduxHooks';
import { openCurrentModal } from 'redux/slices/modals/modals.slice';
import { EModalsNames } from 'shared/types/modal.interface';
import { AUTH_MODAL_DATA } from 'shared/constants/modal-constants';
import { useGetProfileQuery } from 'redux/api/user.api';
import { selectIsAuth } from 'redux/selectors/selectors';
import { CustomMuiIcon } from 'assets/images/icons/customMuiIcons';
import PageContainer from 'pages/PageContainer';

const About: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAuth = useAppSelector(selectIsAuth);
  const { data: profileData = null } = useGetProfileQuery(undefined, { skip: !isAuth });

  const openModal = () => {
    if (profileData) {
      dispatch(openCurrentModal({ popupName: EModalsNames.CreateDebate }));
    } else {
      dispatch(
        openCurrentModal({
          popupName: EModalsNames.AuthModal,
          data: { subtitle: AUTH_MODAL_DATA.subtitleOnChallenge },
        }),
      );
    }
  };

  return (
    <PageContainer showMenu={false}>
      <Stack spacing={8} py={{ xs: 0, md: 4 }} width="100%" maxWidth="1574px" mx="auto">
        <Typography
          component="a"
          onClick={() => navigate('/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'black',
            textDecoration: 'none',
            cursor: 'pointer',
            width: 'fit-content',
            '&:hover': {
              opacity: 0.7,
            },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <CustomMuiIcon type="icon-arrow-left" size="20px" />
            <span>To debates</span>
          </Stack>
        </Typography>

        <Typography variant="h1" fontSize={{ xs: 28, sm: 40, md: 60 }} fontWeight={600}>
          Deb8.tv — A Platform for Live Debates
        </Typography>

        <Stack spacing={2} alignItems="flex-start">
          <Typography fontSize={{ xs: 28, sm: 40, md: 60 }} mb={3}>
            Why
          </Typography>
          <Typography maxWidth={{ xs: '100%', sm: '90%', md: '57%' }}>
            Our world has become polarized, isolated and intolerant. The usual prescription is open
            dialogue and debate, but in a world of echo chambers, what can we do to make that
            possible again?
          </Typography>
          <Typography>Live video debates on Deb8.tv!</Typography>
          <Button
            variant="autoSize"
            onClick={openModal}
            sx={{
              width: { xs: '100%', md: '240px' },
            }}
          >
            Start a debate
          </Button>
        </Stack>

        <Stack spacing={2} alignItems="flex-start">
          <Typography fontSize={{ xs: 28, sm: 40, md: 60 }} mb={3} textAlign="left">
            How
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 4 }}>
            <Stack
              spacing={2}
              alignItems="flex-start"
              sx={{
                bg: 'white',
                p: 3,
                borderRadius: 2,
                flex: 1,
                boxShadow: '0 2px 24px 0 #D2D2D2',
              }}
            >
              <Typography variant="h3" fontSize={{ xs: 22, md: 40 }} textAlign="left">
                Start your own debate
              </Typography>
              <List sx={{ pl: 0 }}>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • Share an idea, concept or question — something you believe in or just want to
                  argue about
                </ListItem>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • Schedule and stream your debate live for an audience
                </ListItem>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • Get paired with an opponent
                </ListItem>
              </List>
            </Stack>

            <Stack
              spacing={2}
              alignItems="flex-start"
              sx={{
                bg: 'white',
                p: 3,
                borderRadius: 2,
                flex: 1,
                boxShadow: '0 2px 24px 0 #D2D2D2',
              }}
            >
              <Typography variant="h3" fontSize={{ xs: 22, md: 40 }} textAlign="left">
                Step in as an opponent
              </Typography>
              <List sx={{ pl: 0 }}>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • Browse challenges, find a topic you disagree with, and accept the challenge
                </ListItem>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • Take the virtual stage and let your arguments shine
                </ListItem>
              </List>
            </Stack>

            <Stack
              spacing={2}
              alignItems="flex-start"
              sx={{
                bg: 'white',
                p: 3,
                borderRadius: 2,
                flex: 1,
                boxShadow: '0 2px 24px 0 #D2D2D2',
              }}
            >
              <Typography variant="h3" fontSize={{ xs: 22, md: 40 }} textAlign="left">
                Join as a spectator or juror
              </Typography>
              <List sx={{ pl: 0 }}>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • Explore debates on topics that interest you
                </ListItem>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • Watch, vote and react in real time
                </ListItem>
              </List>
            </Stack>
          </Stack>
        </Stack>

        <Stack spacing={4} alignItems="flex-start">
          <Typography fontSize={{ xs: 28, sm: 40, md: 60 }} mb={3} textAlign="left">
            Who
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 4 }}>
            <Stack
              spacing={2}
              alignItems="flex-start"
              sx={{
                bg: 'white',
                p: 4,
                borderRadius: 2,
                flex: 1,
                minHeight: { xs: '200px', lg: '300px' },
                boxShadow: '0 2px 24px 0 #D2D2D2',
              }}
            >
              <Typography variant="h3" fontSize={{ xs: 22, md: 40 }} textAlign="left">
                Everybody!
              </Typography>
              <List sx={{ pl: 0 }}>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • Debate your friends or take on strangers
                </ListItem>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • Challenge your favorite politician, blogger or celebrity to a live debate
                </ListItem>
              </List>
            </Stack>

            <Stack
              spacing={2}
              alignItems="flex-start"
              sx={{
                bg: 'white',
                p: 4,
                borderRadius: 2,
                flex: 1,
                minHeight: { xs: '200px', md: '300px' },
                boxShadow: '0 2px 24px 0 #D2D2D2',
              }}
            >
              <Typography variant="h3" fontSize={{ xs: 22, md: 40 }} textAlign="left">
                Soon..
              </Typography>
              <List sx={{ pl: 0 }}>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • You'll have the chance to debate AI avatars of the great thinkers of the past!
                </ListItem>
                <ListItem sx={{ px: 0, fontSize: { xs: 14, md: 20 } }}>
                  • Debate: Buddha, Tolstoy, Nietzsche... or Winnie the Pooh!
                </ListItem>
              </List>
            </Stack>
          </Stack>
        </Stack>

        <Stack spacing={2} alignItems="flex-start" sx={{ mb: '20px' }}>
          <Typography fontSize={{ xs: 28, sm: 40, md: 60 }} mb={3} textAlign="left">
            When
          </Typography>
          <Typography>
            Right now!
            <br />
            Deb8.tv is here to bring back thoughtful, engaging conversations.
            <br />
            Whether you want to debate, challenge or just listen in, there's a place for you.
          </Typography>
          <Typography variant="h3" fontSize={24} mt={2} textAlign="left">
            Ready to dive in?
          </Typography>
          <Button
            variant="autoSize"
            onClick={openModal}
            sx={{
              width: { xs: '100%', md: '240px' },
            }}
          >
            Start a debate
          </Button>
        </Stack>
      </Stack>
    </PageContainer>
  );
};

export default About;
